import {
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ConfirmationDialog from "../../components/ConfirmationDialog";
import { PricingState } from "./PricingState";
import { Selection } from "./types";

type Props = {
  selection: Selection[];
  limit: number;
  /** Stop tracking this competitor entirely. Confirmed here, not by the caller. */
  onDelete: (item: Selection) => void;
  onToggleShown: (item: Selection) => void;
  /**
   * The row currently being written, if any.
   *
   * ⚠️ **It disables every row, not just its own.** Deleting a competitor cascades
   * across the datafeeds, this property's prices and the mapping rows, and two of
   * those running at once deadlocked on the server. The id is still needed to say
   * *which* row is working; what it must not do is leave the others clickable.
   */
  busyId: number | null;
};

/**
 * The right-hand column: what this property tracks.
 *
 * ⚠️ **Two controls, and they are different weights on purpose.** Hiding takes a
 * competitor off the client's own view and is one click back. Deleting removes the
 * rows — the datafeeds, this property's prices and the mapping of which competitor
 * room stands for which of theirs — so it asks first.
 *
 * ⚠️ The §7.6 ladder had a third rung, `is_active` off, reached through a button
 * wearing a bin icon. That icon promised a delete the route did not have; now that it
 * does, the promise and the action are the same thing.
 */
const SelectedList = ({
  selection,
  limit,
  onDelete,
  onToggleShown,
  busyId,
}: Props) => {
  const intl = useIntl();
  const active = selection.filter((item) => item.isActive).length;
  /** The row awaiting confirmation. Held here so the dialog can name it. */
  const [pending, setPending] = useState<Selection | null>(null);

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ mb: 1, justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="subtitle2">
          <FormattedMessage id="COMPETITOR_SELECTION_TITLE" />
        </Typography>
        {/* The allowance, always visible: the add button disables at the cap and a
            count with no denominator makes that look like a bug. */}
        <Chip size="small" label={`${active} / ${limit}`} />
      </Stack>

      {selection.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage id="COMPETITOR_SELECTION_EMPTY" />
        </Typography>
      ) : (
        <List dense disablePadding>
          {selection.map((item) => {
            // ⚠️ Named once and used twice, deliberately. MUI applies a tooltip's
            // title as the accessible name of the element it clones — here the
            // `<span>`, which only exists because a disabled button cannot receive
            // the hover events the tooltip needs. `aria-label` on a bare span is
            // ignored, so the button is left with no accessible name and a screen
            // reader announces "dimmed button" with no hint of what it is or why.
            const shownLabel = intl.formatMessage({
              id: item.isShown
                ? "COMPETITOR_SELECTION_HIDE"
                : "COMPETITOR_SELECTION_SHOW",
            });
            const deleteLabel = intl.formatMessage({
              id: "COMPETITOR_SELECTION_REMOVE",
            });
            return (
              <ListItem
                key={item.id}
                disableGutters
                secondaryAction={
                  item.isSweep ? (
                    <Stack direction="row" spacing={0}>
                      <Tooltip title={shownLabel}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={shownLabel}
                            disabled={busyId !== null}
                            onClick={() => onToggleShown(item)}
                          >
                            {item.isShown ? (
                              <VisibilityIcon fontSize="small" />
                            ) : (
                              <VisibilityOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={deleteLabel}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={deleteLabel}
                            disabled={busyId !== null}
                            onClick={() => setPending(item)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : (
                    // Not ours to remove: the legacy connector owns these rows and
                    // recreates them on its next import.
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        <FormattedMessage id="COMPETITOR_SELECTION_MANAGED" />
                      }
                    />
                  )
                }
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ opacity: item.isActive ? 1 : 0.5 }}
                    >
                      {item.shortName || item.name}
                    </Typography>
                  }
                  secondary={
                    // ⚠️ The client's own toggle wins over the sweep's state. A row
                    // they disabled is not being priced *because they said so*, and
                    // reporting the connector underneath it would answer a question they
                    // did not ask with a word that sounds like a fault.
                    item.isActive ? (
                      <PricingState item={item} />
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        <FormattedMessage id="COMPETITOR_SELECTION_DISABLED" />
                      </Typography>
                    )
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}

      {/* ⚠️ One dialog for the list, not one per row: it is only ever open for the
          row in `pending`, and a dialog per competitor would mount as many hidden
          modals as the property tracks. */}
      <ConfirmationDialog
        open={pending !== null}
        isLoading={pending !== null && busyId === pending.id}
        title={intl.formatMessage({ id: "COMPETITOR_SELECTION_REMOVE" })}
        message={intl.formatMessage(
          { id: "COMPETITOR_SELECTION_REMOVE_CONFIRM" },
          { name: pending?.shortName || pending?.name || "" }
        )}
        onClose={() => setPending(null)}
        onSubmit={() => {
          if (pending) onDelete(pending);
          setPending(null);
        }}
      />
    </Box>
  );
};

export default SelectedList;
